/**
 * How an upload names itself on the file record.
 *
 * A file record carries two separate facts: `originalFilename` — the name of
 * the file the user actually picked, extension included — and `displayName`,
 * the human-facing label a surface shows instead. They are not interchangeable.
 *
 * Both upload paths in this service (`filesApi.uploadFile` and the
 * `useUploadFile` mutation) build their upload-complete payload here so the
 * rule has one definition. They previously each carried their own
 * `options.displayName || file.name` literal, which wrote the label into
 * `originalFilename` and destroyed the real name and its extension —
 * unrecoverably, since nothing else ever recorded it (VALE-761).
 */
export interface UploadCompleteNaming {
  originalFilename: string;
  displayName?: string;
}

/**
 * @param fileName - The uploaded `File`'s own `name` (e.g. `scan_0142.pdf`).
 * @param displayName - Optional human-facing label (e.g. `Authority To Release
 *   Assets Form`). Blank/whitespace-only is treated as absent so an empty form
 *   field never lands as a label; the backend then falls back to the filename.
 */
export function buildUploadCompleteNaming(
  fileName: string,
  displayName?: string
): UploadCompleteNaming {
  const label = displayName?.trim();

  return {
    originalFilename: fileName,
    ...(label ? { displayName: label } : {})
  };
}
